import { Button } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import { BiMessageDetail } from 'react-icons/bi'

import { StatusBadge } from '@/Ship/Components/UI/StatusBadge/StatusBadge'
import { cx } from '@/Ship/Helpers/cssClass'
import type { Notify } from '@/Ship/Contracts/Notify'

import { REQUEST_STATUS_MAP } from '../Contracts/RequestStatus'
import { getRequestExecutorPermissions } from '../Helpers/permissions'
import { statusOption } from '../Helpers/statusOption'
import { getUnitLabel } from '../Helpers/unitLabel'
import { executorDistributed, formatVolume } from '../Helpers/volumeTotals'
import type { VehicleNeedsEditStore } from '../Stores/VehicleNeedsEditStore'
import type { VehicleNeedsViewStore } from '../Stores/VehicleNeedsViewStore'

import { TaskTable } from './TaskTable'

import styles from './RequestFormTemplate.module.css'

/* eslint-disable no-unused-vars -- false positive: ESLint core rule flags param names in function-type signatures */
export interface ExecutorModalOpeners {
    confirmExecution: (task: any) => void
    sendPush: (task: any) => void
    sendMessage: (executor: any) => void
    delegate: (executor: any) => void
    addTask: (executorUuid: string) => void
}

interface ExecutorBlockProps {
    mode: 'view' | 'edit'
    vn: any
    executor: any
    viewStore: VehicleNeedsViewStore
    editStore: VehicleNeedsEditStore
    requestCancelled: boolean
    notify: Notify
    reload: () => void
    openers: ExecutorModalOpeners
}

/**
 * «Розподілено» окремим observer-ом: `pendingChanges` читається тут, а не в ExecutorBlock,
 * тож на кожен символ у комірці маси перемальовується лише цей рядок (інакше таблиця
 * перемонтовує комірки, і інпут губить фокус)
 */
const Distributed = observer(
    ({
        unit,
        assignments,
        editStore,
    }: {
        unit: string
        assignments: any[]
        /** Стор редагування — лише в режимі редагування; у перегляді незбережених змін немає */
        editStore?: VehicleNeedsEditStore | undefined
    }) => (
        <span>
            Розподілено: <b>{formatVolume(executorDistributed(assignments, editStore?.pendingChanges))}</b>{' '}
            {getUnitLabel(unit)}
        </span>
    )
)

/** Виконавець потреби: рядок зведення, дії диспетчера (перегляд) і таблиця завдань */
export const ExecutorBlock = observer(
    ({
        mode,
        vn,
        executor: ex,
        viewStore,
        editStore,
        requestCancelled,
        notify,
        reload,
        openers,
    }: ExecutorBlockProps) => {
        const permissions = getRequestExecutorPermissions(ex)
        const { canTakeInWork, canMarkTransportDistributed, canDelegate, canSendMessage, canEdit } = permissions
        const isView = mode === 'view'
        const assignments: any[] = isView
            ? (ex.assignments ?? [])
            : (editStore.localAssignmentsByExecutor[ex.uuid] ?? ex.assignments ?? [])
        const busy = viewStore.takeInWorkLoading === ex.uuid
        // Стабільна ідентичність: інакше колонки TaskTable перераховуються і комірка-інпут губить фокус
        const onFieldChange = useCallback(
            (uuid: string, field: string, value: any) => editStore.setFieldChange(uuid, field, value),
            [editStore]
        )

        const showActions =
            isView &&
            (((canTakeInWork || canMarkTransportDistributed) && !requestCancelled) ||
                canSendMessage ||
                (canDelegate && !requestCancelled))

        return (
            <div className={cx(styles, 'executor')}>
                <div className={cx(styles, 'executorHead')}>
                    <div className={cx(styles, 'executorFacts')}>
                        <span>
                            Диспетчер: <b>{ex.dispatcher?.fullName || '—'}</b>
                        </span>
                        <span>
                            Потрібно ТЗ: <b>{ex.quantity ?? '—'}</b>
                        </span>
                        <Distributed
                            unit={vn.unit}
                            assignments={assignments}
                            editStore={isView ? undefined : editStore}
                        />
                        <span>
                            Транспорт розподілено: <b>{ex.isTransportDistributed ? 'Так' : 'Ні'}</b>
                        </span>
                        <StatusBadge
                            option={statusOption(REQUEST_STATUS_MAP, ex.status || 'new')}
                            fallbackLabel={ex.status}
                        />
                    </div>
                    {showActions && (
                        <div className={cx(styles, 'executorActions')}>
                            {canTakeInWork && !requestCancelled && (
                                <Button
                                    size="small"
                                    type="primary"
                                    loading={busy}
                                    onClick={() => viewStore.changeExecutorStatus(ex.uuid, 'in_work', notify, reload)}
                                >
                                    Взяти в роботу
                                </Button>
                            )}
                            {canMarkTransportDistributed && !requestCancelled && (
                                <Button
                                    size="small"
                                    type="primary"
                                    loading={busy}
                                    onClick={() =>
                                        viewStore.clickTransportDistributed(ex.uuid, assignments, vn, notify, reload)
                                    }
                                >
                                    Транспорт розподілено
                                </Button>
                            )}
                            {canSendMessage && (
                                <Button size="small" icon={<BiMessageDetail />} onClick={() => openers.sendMessage(ex)}>
                                    Повідомлення
                                </Button>
                            )}
                            {canDelegate && !requestCancelled && (
                                <Button size="small" onClick={() => openers.delegate(ex)}>
                                    Делегувати
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {assignments.length === 0 ? (
                    <div className={cx(styles, 'empty')}>Завдань ще немає</div>
                ) : (
                    <TaskTable
                        mode={mode}
                        vn={vn}
                        assignments={assignments}
                        permissions={permissions}
                        invalidAssignments={viewStore.invalidAssignments}
                        onFieldChange={isView ? undefined : onFieldChange}
                        allBusy={isView && viewStore.changingStatus}
                        busyTaskUuid={isView ? null : editStore.deletingTaskUuid}
                        actions={{
                            changeStatus: isView
                                ? (task, status) => viewStore.changeStatus(task.uuid, status, notify, reload)
                                : undefined,
                            confirmExecution: isView ? openers.confirmExecution : undefined,
                            sendPush: isView ? openers.sendPush : undefined,
                            deleteTask: isView ? undefined : (task) => editStore.deleteTask(task.uuid, ex.uuid, notify),
                        }}
                    />
                )}

                {!isView && canEdit && (
                    <Button
                        block
                        icon={<PlusOutlined />}
                        className={cx(styles, 'addTask')}
                        onClick={() => openers.addTask(ex.uuid)}
                    >
                        Додати завдання
                    </Button>
                )}
            </div>
        )
    }
)
